import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

export interface AvatarPerson {
  id: string
  displayName: string
  photoUrl?: string | null
}

const SIZE = {
  xs: 'size-5 text-micro',
  sm: 'size-6 text-tiny',
  md: 'size-8 text-xs',
  /** The card footer: big enough to recognise a face, small enough not to crowd the title. */
  card: 'size-7 text-tiny',
} as const

export function UserAvatar({
  person,
  size = 'sm',
  shape = 'round',
  className,
}: {
  person: AvatarPerson
  size?: keyof typeof SIZE
  /** Square is a softly rounded tile — the card footer's look. */
  shape?: 'round' | 'square'
  className?: string
}) {
  return (
    <Avatar
      className={cn(SIZE[size], 'border border-border-soft', shape === 'square' && 'rounded-[8px] [&_*]:rounded-none', className)}
    >
      {person.photoUrl ? (
        // Google photo URLs 403 without no-referrer.
        <AvatarImage
          src={person.photoUrl}
          alt={person.displayName}
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : null}
      {/*
        No photo: the TapTalent.ai mark, not initials — the company mark, not the Tapwork
        product logo, which brands the tool rather than standing in for a person.

        Worth knowing what this costs — initials told two unphotographed people apart at a
        glance and the mark does not, so the name beside the avatar is now doing all of that
        work. Where an avatar appears without a name next to it, keep the tooltip.
      */}
      <AvatarFallback className="bg-transparent p-0">
        <img
          src="/brand/Taptalent.ai%20Logo.png"
          alt={person.displayName}
          title={person.displayName}
          className="size-full object-cover"
        />
      </AvatarFallback>
    </Avatar>
  )
}

export function UserAvatarStack({
  people,
  max = 3,
  size = 'sm',
  className,
}: {
  people: AvatarPerson[]
  max?: number
  size?: keyof typeof SIZE
  className?: string
}) {
  if (people.length === 0) return null
  const shown = people.slice(0, max)
  const overflow = people.length - shown.length

  return (
    <div className={cn('flex -space-x-1.5', className)}>
      {shown.map((person) => (
        <Tooltip key={person.id}>
          <TooltipTrigger asChild>
            <span className="ring-2 ring-card rounded-full">
              <UserAvatar person={person} size={size} />
            </span>
          </TooltipTrigger>
          <TooltipContent>{person.displayName}</TooltipContent>
        </Tooltip>
      ))}
      {overflow > 0 ? (
        <span
          className={cn(
            SIZE[size],
            'flex items-center justify-center rounded-full bg-muted font-medium text-text-secondary ring-2 ring-card',
          )}
        >
          +{overflow}
        </span>
      ) : null}
    </div>
  )
}
